import type { useTranslations } from "next-intl";

// Error codes the cart answers an invalid sub-items selection with (422,
// ValidateCartSubItems / MPL-3694). The selection is validated before anything
// is written, so on any of these the cart is unchanged and the local selection
// has to be rolled back.
const SUB_ITEMS_ERROR_CODES = [
  "invalid_sub_item",
  "invalid_sub_item_type",
  "invalid_sub_item_id",
  "invalid_sub_item_quantity",
  "sub_item_date_not_in_diet_dates",
  "duplicated_sub_item",
] as const;

// Same for the Foodango products of a position (422, ValidateCartProductItems /
// MPL-3829): validated up front, cart untouched on rejection.
const PRODUCT_ITEMS_ERROR_CODES = [
  "invalid_product_item",
  "invalid_product_item_quantity",
  "product_item_without_parent_not_supported",
  "product_item_orphaned",
  "product_item_date_not_in_diet_dates",
  "duplicated_product_item",
  "product_items_not_allowed_for_multidiet",
  "product_merchant_mismatch",
] as const;

// The product is gone from the diet's offer (withdrawn, deactivated or excluded
// for this catering) — the one rejection the user can act on by picking
// something else, so it gets its own message. Raised both by the validation
// and, for a product that vanished between two writes, by the pricing.
const PRODUCT_UNAVAILABLE_ERROR_CODES = [
  "product_not_available",
  "product_items_not_priced",
] as const;

type AddonsErrorCode =
  | (typeof SUB_ITEMS_ERROR_CODES)[number]
  | (typeof PRODUCT_ITEMS_ERROR_CODES)[number]
  | (typeof PRODUCT_UNAVAILABLE_ERROR_CODES)[number];

const ADDONS_ERROR_CODES: readonly string[] = [
  ...SUB_ITEMS_ERROR_CODES,
  ...PRODUCT_ITEMS_ERROR_CODES,
  ...PRODUCT_UNAVAILABLE_ERROR_CODES,
];

const isAddonsErrorCode = (value: unknown): value is AddonsErrorCode =>
  typeof value === "string" && ADDONS_ERROR_CODES.includes(value);

// The error code lives in the forwarded response body carried on ApiError.data.
const getErrorCode = (error: unknown): AddonsErrorCode | null => {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return null;
  }

  const { data } = error;
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return null;
  }

  return isAddonsErrorCode(data.error) ? data.error : null;
};

export const isSubItemsError = (error: unknown): boolean =>
  getErrorCode(error) !== null;

// Message for a failed addon update: a translated one for a known rejection,
// otherwise whatever the request already reported. The sub-item and product
// codes all map to the same user-facing text — the distinctions are for us, not
// the user, who can only be told the selection could not be saved. The one
// exception is a product no longer on offer.
export const getAddonsErrorMessage = (
  error: unknown,
  t: ReturnType<typeof useTranslations>,
): string => {
  const code = getErrorCode(error);

  if (
    code !== null &&
    (PRODUCT_UNAVAILABLE_ERROR_CODES as readonly string[]).includes(code)
  ) {
    return t("cart.addons.errors.productUnavailable");
  }

  if (code !== null) {
    return t("cart.addons.errors.invalidSelection");
  }

  return error instanceof Error ? error.message : t("cart.addons.errors.save");
};
